import { Box, Button, ButtonProps, CircularProgress, Grid, Stack, Typography } from "@compass/core";
import { Student, StudentCustomIdType } from "generated/openApi";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import { newToast } from "@compass/core";
import ApiService from "services/apiService";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import { useEffect, useRef, useState } from "react";

const secondaryButtonSx: ButtonProps["sx"] = {
    paddingX: "24px",
    whiteSpace: "nowrap",
    border: (theme) => `1.5px solid ${theme.palette.border.dark}`,
    backgroundColor: (theme) => theme.palette.common.white,
    color: (theme) => theme.palette.text.primary,
    "&:hover": {
        // No theme token matches the specified subtle hover background.
        backgroundColor: "rgba(0,0,0,0.03)",
        borderColor: (theme) => theme.palette.primary.main,
    },
};

interface StudentQrPageBodyProps {
    student: Student;
    customIdTypes: StudentCustomIdType[];
    setStudent: (updater: (draft: Student) => void) => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
}

export const StudentQrPageBody = (props: StudentQrPageBodyProps) => {
    const dispatch = useDispatch();
    const [isEmailingWalletPass, setIsEmailingWalletPass] = useState(false);
    const [isDownloadingBadge, setIsDownloadingBadge] = useState(false);
    const autoRegenAttemptedRef = useRef<number | null>(null);

    //Check if QR code exists
    const hasQrCode = !!(props.student.qrCodeUploadFile?.fileRef && props.student.qrCodeUploadFile?.fileName);

    // student must have an email address on file to receive the Mobile Pass.
    const hasEmail = !!props.student.email?.trim();

    const customId1Type = props.customIdTypes.find((customIdType) => customIdType.id === 1);
    const customId1Label = customId1Type?.name;

    const isOldUrlFormat = (): boolean => {
        return hasQrCode && !props.student.qrCodeUploadFile?.imagePreviewData;
    };

    //Generate QR code if it doesn't exist
    const generateQrCode = async () => {
        if (!props.student.id) return;

        const studentId = props.student.id;
        props.setIsLoading(true);
        try {
            const response = await ApiService.students.generateQrCode(studentId);

            //Update student with new QR code
            props.setStudent((draft) => {
                if (draft.id === studentId) draft.qrCodeUploadFile = response;
            });

            const isRegeneration = hasQrCode;
            const message = isRegeneration
                ? "QR Code updated successfully"
                : "QR Code generated successfully";
            dispatch(setAppToast(newToast("success", message)));
        } catch (error) {
            dispatch(setAppToast(newToast("error", "Failed to generate QR Code")));
        } finally {
            props.setIsLoading(false);
        }
    };

    useEffect(() => {
        const studentId = props.student.id;

        if (autoRegenAttemptedRef.current !== null && autoRegenAttemptedRef.current !== studentId) {
            autoRegenAttemptedRef.current = null;
        }

        if (
            studentId &&
            autoRegenAttemptedRef.current !== studentId &&
            isOldUrlFormat() &&
            !props.isLoading
        ) {
            autoRegenAttemptedRef.current = studentId;
            generateQrCode();
        }
    }, [props.student.id, props.student.qrCodeUploadFile, props.isLoading]);

    const needsQrRetry = isOldUrlFormat() &&
        autoRegenAttemptedRef.current === props.student.id && !props.isLoading;

    const handleDownload = () => {
        if (!props.student.qrCodeUploadFile?.imagePreviewData) return;
        const { imagePreviewData } = props.student.qrCodeUploadFile;
        const studentName = props.student.fullName;
        const studentId = props.student.customId1;
        const fileName = `QR_${studentName}_${studentId}.png`;
        const link = document.createElement("a");
        link.href = imagePreviewData;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadBadge = async () => {
        if (!props.student.id) return;

        setIsDownloadingBadge(true);
        let objectUrl: string | undefined;

        try {
            const response = await ApiService.students.downloadQrBadge(props.student.id);
            if (!response?.data) throw new Error("Mobile Pass PDF was not returned.");

            objectUrl = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = response.fileName || `Mobile_Pass_${props.student.fullName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Mobile Pass Download Error:", error);
            dispatch(setAppToast(newToast("error", "Failed to download Mobile Pass.")));
        } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setIsDownloadingBadge(false);
        }
    };

    const handleEmailWalletPass = async () => {
        if (!hasEmail) {
            const name = props.student.fullName || "This student";
            dispatch(
                setAppToast(
                    newToast(
                        "error",
                        `${name} does not have an email address on file. Add an email address to the student record to send the Mobile Pass.`
                    )
                )
            );
            return;
        }

        setIsEmailingWalletPass(true);
        try {
            // Using the generated ApiService client!
            await ApiService.walletPass.emailWalletPass(props.student.id);

            dispatch(setAppToast(newToast("success", "Mobile Pass emailed successfully.")));
        } catch (error) {
            console.error("Wallet Pass Error:", error);
            dispatch(setAppToast(newToast("error", "Failed to email Mobile Pass.")));
        } finally {
            setIsEmailingWalletPass(false);
        }
    };

    return hasQrCode ? (
        <Grid container spacing="xl" alignItems="center" width="100%">
            <Grid item xs={12} md={5} sx={{ display: "flex", justifyContent: "center" }}>
                {props.student.qrCodeUploadFile?.imagePreviewData && (
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            minWidth: "212px",
                            padding: "16px",
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "12px",
                            boxShadow: theme.palette.shadow.elevatedCard,
                        })}
                    >
                        <Box
                            component="img"
                            src={props.student.qrCodeUploadFile.imagePreviewData}
                            alt="Student QR Code"
                            sx={{ width: "180px", minWidth: "180px", height: "180px", objectFit: "contain" }}
                        />
                    </Box>
                )}
                {needsQrRetry && (
                    <Stack spacing="sm" alignItems="center">
                        <Typography variant="body2">QR code couldn't be refreshed.</Typography>
                        <Button onClick={generateQrCode} disabled={props.isLoading}>Retry</Button>
                    </Stack>
                )}
            </Grid>

            <Grid item xs={12} md={7} sx={{ paddingRight: (theme) => theme.spacing(6) }}>
                <Stack spacing="xl" alignItems="center" justifyContent="center" width="100%">
                    <Stack alignItems="center" sx={{ textAlign: "center" }}>
                        <Typography
                            variant="h5"
                            sx={(theme) => ({ fontWeight: "bold", color: theme.palette.text.primary })}
                        >
                            {props.student.fullName}
                        </Typography>
                        {props.student.customId1 && customId1Type && (
                            <Typography
                                variant="body2"
                                sx={(theme) => ({ color: theme.palette.text.secondary, whiteSpace: "nowrap" })}
                            >
                                {customId1Label ? `${customId1Label}: ${props.student.customId1}` : props.student.customId1}
                            </Typography>
                        )}
                    </Stack>

                    <Stack direction="row" spacing="md" width="100%">
                        {customId1Type?.isSsn === false && (
                            <Button
                                fullWidth
                                variant="outlined"
                                textTransform="none"
                                onClick={handleDownloadBadge}
                                disabled={isDownloadingBadge}
                                startIcon={isDownloadingBadge ? <CircularProgress size={16} /> : <PrintIcon />}
                                sx={secondaryButtonSx}
                            >
                                {isDownloadingBadge ? "Preparing Mobile Pass..." : "Print Mobile Pass"}
                            </Button>
                        )}
                        <Button
                            fullWidth
                            variant="outlined"
                            textTransform="none"
                            onClick={handleDownload}
                            disabled={!props.student.qrCodeUploadFile?.imagePreviewData}
                            startIcon={<FileDownloadIcon />}
                            sx={secondaryButtonSx}
                        >
                            Download QR
                        </Button>
                    </Stack>

                    <Stack spacing="md" alignItems="center" width="100%">
                        <Button
                            fullWidth
                            variant="contained"
                            textTransform="none"
                            onClick={handleEmailWalletPass}
                            disabled={isEmailingWalletPass}
                            startIcon={
                                isEmailingWalletPass ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <Box
                                            component="img"
                                            src="/images/apple-logo.svg"
                                            alt=""
                                            onError={(event) => {
                                                event.currentTarget.style.display = "none";
                                            }}
                                            sx={{
                                                width: "17px",
                                                height: "17px",
                                                objectFit: "contain",
                                                filter: "brightness(0) invert(1)",
                                            }}
                                        />
  
                                    </Box>
                                )
                            }
                            sx={{
                                whiteSpace: "nowrap",
                                backgroundColor: (theme) => theme.palette.primary.main,
                                boxShadow: "0 2px 6px rgba(8,81,156,0.3)",
                                fontWeight: "bold",
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light,
                                },
                            }}
                        >
                            {isEmailingWalletPass ? "Sending..." : "Send Mobile Pass"}
                        </Button>
                        <Typography
                            variant="caption"
                            textAlign="center"
                            sx={(theme) => ({ color: theme.palette.text.secondary })}
                        >
                            Email Mobile Pass to student
                        </Typography>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    ) : (
        <Stack spacing="lg" alignItems="center">
            <Typography variant="body1" color="textSecondary" textAlign="center">
                No QR Code exists for {props.student.fullName || "this student"}
            </Typography>
            <Button
                variant="contained"
                onClick={generateQrCode}
                disabled={props.isLoading}
            >
                {props.isLoading ? (
                    <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Generating...
                    </>
                ) : (
                    "Generate QR Code"
                )}
            </Button>
        </Stack>
    );
};
